import { AppDispatch } from "app/store";
import OmronFooter from "components/OmronFooter";
import OmronHeader from "components/OmronHeader";
import OmronSidebar from "components/OmronSidebar";
import {
  activateLicenseAsync,
  activateTrialLicenseAsync,
  initialAsync,
  updateAsync,
  transferLicenseAsync,
} from "features/software";
import { PAGE_SIZE } from "features/software/constants";
import {
  selectEntitlements,
  selectExcuteState,
  selectLoadState,
  selectMaxPage,
  selectPageIndex,
  selectPopupTarget,
  selectTransferTarget,
  selectSearchText,
} from "features/software/selectors";
import {
  changeNowIndex,
  changePopupTarget,
  changeSearchText,
  clearPopupTarget,
  changeTransferTarget,
  clearTransferTarget,
} from "features/software/softwareSlice";
import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { selectUserState } from "features/auth/selectors";
import { logoutAsync, userAsync } from "features/auth/operations";
import { useTranslation } from "react-i18next";
import PageTitle from "components/PageTitle";
import SoftwareSearch from "./SoftwareSearch";
import SoftwareTable from "./SoftwareTable";
import ActivatePopup from "./ActivatePopup";
import TransferLicensePopup from "./TransferLicensePopup";

const SoftwarePage: React.FC = (): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();

  const user = useSelector(selectUserState);

  const searchText = useSelector(selectSearchText);

  const loadState = useSelector(selectLoadState);

  const entitlements = useSelector(selectEntitlements);
  const maxpage = useSelector(selectMaxPage);
  const pageIndex = useSelector(selectPageIndex);
  const popupTarget = useSelector(selectPopupTarget);
  const transferTarget = useSelector(selectTransferTarget);
  const excuteState = useSelector(selectExcuteState);
  const onPageChange = useCallback(
    (x: { selected: number }) => {
      // storeのnowIndexを更新する。
      dispatch(changeNowIndex({ pegeNumber: x.selected }));
      // 選択された数字でAPIを叩く
      dispatch(
        updateAsync({
          pageSize: PAGE_SIZE,
          pageIndex: x.selected,
          searchText,
        })
      );
    },
    [dispatch, searchText]
  );

  const onActivate = useCallback(
    async (
      pkId: string,
      eId: string,
      isTrial: boolean,
      isNodeLocked: boolean
    ) => {
      if (isTrial && !isNodeLocked) {
        // eslint-disable-next-line no-alert
        const ok = window.confirm(t("softwarePage.text.trialActivate"));
        if (ok) {
          const { meta } = await dispatch(activateTrialLicenseAsync({ pkId }));
          // 処理が成功したら画面表示されている内容をリロード
          // 実装的に綺麗ではないが、結果によって画面更新するかを分けたいためdispatchの返り値を使用して呼び分ける
          if (meta.requestStatus === "fulfilled") {
            await dispatch(
              updateAsync({
                pageSize: PAGE_SIZE,
                pageIndex,
                searchText,
              })
            );
          }
        }
      } else {
        dispatch(changePopupTarget({ eId }));
      }
    },
    [dispatch, pageIndex, searchText, t]
  );
  const onExcute = useCallback(
    async (pkId: string, fileName: string, fingerPrint: string) => {
      const { meta } = await dispatch(
        activateLicenseAsync({
          pkId,
          fileName,
          fingerPrint,
        })
      );
      if (meta.requestStatus === "fulfilled") {
        await dispatch(
          updateAsync({
            pageSize: PAGE_SIZE,
            pageIndex,
            searchText,
          })
        );
      }
    },
    [dispatch, pageIndex, searchText]
  );

  const onTransfer = useCallback(
    async (eId: string) => {
      dispatch(changeTransferTarget({ eId }));
    },
    [dispatch]
  );

  const onTransferExcute = useCallback(
    async (email: string, eId: string) => {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(t("softwarePage.text.transferLicense"));
      if (ok) {
        const { meta } = await dispatch(transferLicenseAsync({ email, eId }));
        if (meta.requestStatus === "fulfilled") {
          await dispatch(
            updateAsync({
              pageSize: PAGE_SIZE,
              pageIndex,
              searchText,
            })
          );
        }
      }
    },
    [dispatch, pageIndex, searchText, t]
  );

  // 検索ボタン押下時に検索文字列を反映
  const onClickSearch = useCallback(
    (text: string) => {
      dispatch(changeSearchText({ text }));
      dispatch(
        updateAsync({
          pageSize: PAGE_SIZE,
          pageIndex,
          searchText: text,
        })
      );
    },
    [dispatch, pageIndex]
  );

  // 初期読込
  useEffect(() => {
    dispatch(userAsync());
    dispatch(initialAsync());
  }, [dispatch]);

  const logout = useCallback(async () => {
    const { meta } = await dispatch(logoutAsync());
    if (meta.requestStatus === "rejected") {
      return false;
    }
    window.location.pathname = "/";
    return true;
  }, [dispatch]);

  return (
    <div>
      {popupTarget && (
        <ActivatePopup
          eId={popupTarget.eId}
          name={popupTarget.name}
          onCancel={() => {
            dispatch(clearPopupTarget());
          }}
          onExcute={onExcute}
          pkId={popupTarget.pkId}
          version={popupTarget.version}
          loading={excuteState}
        />
      )}
      {transferTarget && (
        <TransferLicensePopup
          title={t("softwarePage.label.transferPopupTitle")}
          eId={transferTarget.eId}
          name={transferTarget.name}
          onCancel={() => {
            dispatch(clearTransferTarget());
          }}
          onExcute={onTransferExcute}
          pkId={transferTarget.pkId}
          version={transferTarget.version}
          loading={excuteState}
        />
      )}
      <Wrap>
        <OmronHeader
          link={{ label: t("common.label.breadcrumbsHome"), path: "/" }}
          label={[t("common.label.breadcrumbsSoftware")]}
          user={user}
        />
        <OmronSidebar software onLogout={logout} />
        <Main>
          <PageTitle title={t("softwarePage.label.title")} text="" />
          <SoftwareSearch onClick={onClickSearch} />
          <SoftwareTable
            loadState={loadState}
            dataList={entitlements}
            maxPage={maxpage}
            pageIndex={pageIndex}
            onPageChange={onPageChange}
            activate={onActivate}
            transferLicense={onTransfer}
          />
        </Main>
        <OmronFooter />
      </Wrap>
    </div>
  );
};

export default SoftwarePage;

const Wrap = styled.div`
  display: grid;
  grid-template-rows: 76px 40px 1fr auto;
  grid-template-columns: 20rem 1fr;
  grid-template-areas: "header header" "breadc breadc" "side   main" "side   footer";
  min-height: 100vh;
`;
const Main = styled.main`
  grid-area: main;
  padding: 3.2rem 50px;
`;
